import type { HubPermissionKey, PortalFeatureKey, Role } from "@wwrp/shared";
import {
  IconAlertTriangle,
  IconBulb,
  IconChecklist,
  IconClipboardList,
  IconKey,
  IconLayoutDashboard,
  IconListDetails,
  IconLogs,
  IconServer,
  IconSettings,
  IconShieldLock,
  IconUser,
  IconUsers,
  IconWorld,
  IconSearch,
} from "@tabler/icons-react";

export const NAV_CATEGORIES = [
  { id: "home", label: "Home", icon: IconLayoutDashboard, sections: ["Overview", "Your Account"] },
  {
    id: "staff",
    label: "Staff",
    icon: IconUsers,
    sections: ["Staff Operations", "Admin"],
  },

  { id: "community", label: "Community", icon: IconWorld, sections: ["Updates", "Participate"] },
  { id: "admin", label: "Admin", icon: IconShieldLock, sections: ["Operations", "Access Control"] },
] as const;
export type NavCategoryId = (typeof NAV_CATEGORIES)[number]["id"];

export interface NavPage {
  category: NavCategoryId;
  id: string;
  to: string;
  label: string;
  description: string;
  keywords: string[];
  icon: React.ElementType;
  end: boolean;
  minRole: Role | null;
  /**
   * When set, nav visibility is driven by the user's resolved permissions map
   * (`user.permissions[permissionKey]`) rather than the static `minRole` check.
   * This means changes made through the Portal permissions UI are reflected here
   * without a redeploy.
   */
  permissionKey?: HubPermissionKey;
  /** Hidden everywhere when the matching global feature is switched off. */
  featureKey?: PortalFeatureKey;
  /** Included in Portal Features but not shown as a sidebar destination. */
  navigationHidden?: boolean;
  /** Included in Portal Features without a corresponding route. */
  featureOnly?: boolean;
  /** Visible only to users with Sysadmin access. */
  sysadminOnly?: boolean;
  /** Section heading shown above the first item in a new group. Undefined = no heading. */
  section?: string;
}

export const NAV_PAGES: NavPage[] = [
  {
    id: "applications",
    category: "home",
    section: "Your Account",
    to: "/applications",
    label: "Applications",
    description: "Reserve and transfer applications",
    keywords: ["reserve", "transfer", "apply", "forms"],
    icon: IconClipboardList,
    end: false,
    minRole: null,
    navigationHidden: true,
    featureOnly: true,
  },
  {
    id: "users",
    category: "admin",
    section: "Operations",
    to: "/admin/users",
    label: "Users",
    description: "Browse everyone who has signed into the portal",
    keywords: ["users", "accounts", "sign-ins", "logins"],
    icon: IconUsers,
    end: false,
    minRole: "administration",
    permissionKey: "users.view",
  },
  {
    id: "da-rules",
    category: "admin",
    section: "Operations",
    to: "/admin/da-rules",
    label: "DA Rules",
    description: "Import rule headings from a Google Doc",
    keywords: ["disciplinary", "rules", "google docs", "import"],
    icon: IconListDetails,
    end: true,
    minRole: "administration",
    permissionKey: "da_rules.manage",
  },
  {
    id: "application-management",
    category: "admin",
    section: "Operations",
    to: "/admin/applications",
    label: "Manage Applications",
    description: "Create application forms and manage department questions and availability",
    keywords: ["applications", "templates", "create", "questions", "forms"],
    icon: IconSettings,
    end: false,
    minRole: "senior_administration",
    featureKey: "applications",
  },
  {
    id: "portal-features",
    category: "admin",
    section: "Operations",
    to: "/admin/features",
    label: "Portal Features",
    description: "Turn Portal sections on or off for everyone",
    keywords: ["features", "settings", "forms", "disable", "enable"],
    icon: IconSettings,
    end: false,
    minRole: "administration",
    permissionKey: "portal_features.manage",
  },
  {
    id: "discord-logs",
    category: "admin",
    section: "Operations",
    to: "/admin/discord-logs",
    label: "Discord Logs",
    description: "Choose Portal events to send to Discord channels and webhooks",
    keywords: ["discord", "webhooks", "logs", "events", "benefits", "audit"],
    icon: IconLogs,
    end: false,
    minRole: "senior_administration" as Role,
    permissionKey: "discord_logs.manage",
  },
  {
    id: "dashboard",
    category: "home",
    section: "Overview",
    to: "/",
    label: "Dashboard",
    description: "Announcements, community stats, and who is online",
    keywords: ["home", "announcements", "overview", "stats", "online"],
    icon: IconLayoutDashboard,
    end: true,
    minRole: null,
  },
  {
    id: "my-profile",
    category: "home",
    section: "Your Account",
    to: "/my-profile",
    label: "My Profile",
    description: "Your profile, records, and LOA requests",
    keywords: ["me", "profile", "loa", "conduct", "records"],
    icon: IconUser,
    end: false,
    minRole: null,
  },
  {
    id: "settings",
    category: "home",
    section: "Your Account",
    to: "/settings",
    label: "Settings",
    description: "Manage your account and notification preferences",
    keywords: ["preferences", "notifications", "discord", "account"],
    icon: IconSettings,
    end: false,
    minRole: null,
  },
  {
    id: "my-requests",
    category: "home",
    section: "Your Account",
    to: "/my-requests",
    label: "My Forms",
    description: "Track your applications, LOAs, and name change requests",
    keywords: ["my", "forms", "requests", "loa", "name change", "applications", "history"],
    icon: IconClipboardList,
    end: false,
    minRole: null,
  },
  {
    id: "community",
    category: "community",
    section: "Updates",
    to: "/community",
    label: "Community",
    description: "Bulletins, leadership updates, and top playtime leaderboard",
    keywords: ["community", "bulletins", "updates", "playtime", "leaderboard", "priorities"],
    icon: IconWorld,
    end: false,
    minRole: null,
  },
  {
    id: "feedback",
    category: "community",
    section: "Participate",
    to: "/feedback",
    label: "Feedback",
    description: "Submit ideas, vote on features, and track the roadmap",
    keywords: ["feedback", "ideas", "feature", "request", "vote", "roadmap", "bugs", "suggestions"],
    icon: IconBulb,
    end: false,
    minRole: null,
  },
  {
    id: "members",
    category: "staff",
    to: "/members",
    label: "Staff Lookup",
    description: "Search and view staff profiles",
    keywords: ["search", "list", "staff", "profiles", "communityid", "cid"],
    icon: IconUsers,
    end: false,
    minRole: "staff_in_training" as Role,
    permissionKey: "member.view_list",
    section: "Staff Operations",
  },
  {
    id: "server-players",
    category: "admin",
    section: "Operations",
    to: "/admin/server",
    label: "Live Players",
    description: "See who is currently online on the FiveM server",
    keywords: ["server", "players", "online", "live", "fivem", "kick", "in-game"],
    icon: IconServer,
    end: false,
    minRole: "staff" as Role,
    permissionKey: "server_players.manage",
  },
  {
    id: "compliance",
    category: "staff",
    section: "Admin",
    to: "/admin/compliance",
    label: "Compliance",
    description: "Review and remediate non-compliant members",
    keywords: ["non-compliant", "audit", "members", "remediation"],
    icon: IconAlertTriangle,
    end: false,
    minRole: "staff" as Role,
    permissionKey: "compliance.view",
  },
  {
    id: "pending-approvals",
    category: "staff",
    section: "Staff Operations",
    to: "/admin/pending-approvals",
    label: "Pending Approvals",
    description: "Review LOAs and requested updates awaiting approval",
    keywords: ["approvals", "pending", "review", "loa", "requests"],
    icon: IconChecklist,
    end: false,
    minRole: "staff_in_training" as Role,
    permissionKey: "da_update.request",
  },
  {
    id: "audit-log",
    category: "admin",
    to: "/admin/audit-log",
    label: "Audit Log",
    description: "Review all staff actions across the platform",
    keywords: ["logs", "history", "activity", "actions"],
    icon: IconLogs,
    end: false,
    minRole: "administration" as Role,
    permissionKey: "audit_log.view",
    section: "Operations",
  },
  {
    id: "role-mappings",
    category: "admin",
    section: "Access Control",
    to: "/role-mappings",
    label: "Role Mappings",
    description: "Map Discord roles to Portal permission roles",
    keywords: ["discord", "permissions", "roles", "mapping"],
    icon: IconKey,
    end: false,
    minRole: "head_administration" as Role,
    permissionKey: "role_mappings.manage",
  },
  {
    id: "hub-permissions",
    category: "admin",
    section: "Access Control",
    to: "/admin/hub-permissions",
    label: "Portal Permissions",
    description: "Configure minimum role requirements for every Portal action",
    keywords: ["permissions", "roles", "access", "control", "configure", "gate"],
    icon: IconShieldLock,
    end: false,
    // Hard-coded private permission — not a configurable role threshold.
    minRole: null,
    sysadminOnly: true,
  },
  {
    id: "admin-requests",
    category: "staff",
    section: "Admin",
    to: "/admin/requests",
    label: "Requests",
    description: "Request approval for documents, work, and policy changes",
    keywords: ["admin", "requests", "documents", "policy", "approval", "review"],
    icon: IconClipboardList,
    end: false,
    minRole: "junior_administration" as Role,
    permissionKey: "admin_request.view",
  },
  {
    id: "benefits",
    category: "staff",
    section: "Staff Operations",
    to: "/__features/benefits",
    label: "Benefits",
    description: "Benefits recorded on staff member profiles",
    keywords: ["benefits", "rewards", "member records"],
    icon: IconChecklist,
    end: true,
    minRole: null,
    navigationHidden: true,
    featureOnly: true,
  },
  {
    id: "restrictions",
    category: "staff",
    section: "Staff Operations",
    to: "/__features/restrictions",
    label: "Restrictions",
    description: "Restrictions recorded on staff member profiles",
    keywords: ["restrictions", "holds", "member records"],
    icon: IconShieldLock,
    end: true,
    minRole: null,
    navigationHidden: true,
    featureOnly: true,
  },
  {
    id: "member-notes",
    category: "staff",
    section: "Staff Operations",
    to: "/__features/member-notes",
    label: "Member Notes",
    description: "Internal notes on staff member profiles",
    keywords: ["notes", "member records", "staff"],
    icon: IconClipboardList,
    end: true,
    minRole: null,
    navigationHidden: true,
    featureOnly: true,
  },
  {
    id: "data-sync",
    category: "admin",
    section: "Operations",
    to: "/__features/data-sync",
    label: "Data Sync",
    description: "Staff and disciplinary record synchronization tools",
    keywords: ["data", "sync", "sheets", "conflicts"],
    icon: IconLogs,
    end: true,
    minRole: null,
    navigationHidden: true,
    featureOnly: true,
  },
  {
    id: "search-das",
    category: "staff",
    section: "Staff Operations",
    to: "/discipline/search",
    label: "Search DAs",
    description: "Search by Case # and browse recent disciplinary actions",
    keywords: ["da", "discipline", "number", "lookup", "search", "recent", "records"],
    icon: IconSearch,
    end: true,
    minRole: null,
    permissionKey: "da.view",
  },
  {
    id: "application-review",
    category: "staff",
    section: "Staff Operations",
    to: "/staff/applications",
    label: "Application Review",
    description: "Review reserve and transfer applications",
    keywords: ["applications", "review", "reserve", "transfer", "approve"],
    icon: IconChecklist,
    end: false,
    minRole: "staff_in_training",
    featureKey: "applications",
  },
];

export function getPortalFeatureKey(
  page: Pick<NavPage, "id" | "featureKey">,
): PortalFeatureKey | null {
  if (page.id === "portal-features") return null;
  return page.featureKey ?? page.id;
}

function matchesPagePath(page: NavPage, pathname: string): boolean {
  const routeSegments = page.to.split("/").filter(Boolean);
  const pathSegments = pathname.split("/").filter(Boolean);
  if (
    page.end
      ? pathSegments.length !== routeSegments.length
      : pathSegments.length < routeSegments.length
  )
    return false;
  return routeSegments.every((segment, index) =>
    segment.startsWith(":")
      ? (pathSegments[index] ?? "").length > 0
      : segment === pathSegments[index],
  );
}

export function getNavPageForPathname(pathname: string): NavPage | undefined {
  const normalizedPath = pathname.replace(/\/+$/, "") || "/";
  return [...NAV_PAGES]
    .filter((page) => !page.featureOnly && matchesPagePath(page, normalizedPath))
    .sort((left, right) => right.to.length - left.to.length)[0];
}
